import Text from 'components/shared/Text'
import Input, { InputType } from 'components/shared/Input'
import Section from 'components/shared/Section'
import { FormattedMessage, useIntl } from 'react-intl'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { ContactField, setContactInfo } from 'store/order-slice'
import { useErrorMessages } from 'utils/hooks/useErrorMessages'
import { useGetUserSessionQuery } from 'store/auth-slice/hooks'
import { useApp } from 'store/steps'
import { useEffect } from 'react'
import Invoice from 'routes/OrderAndPayment/sections/Invoice'
import { ContactFormWrapper } from './style'

const ContactData = () => {
    const { formatMessage } = useIntl()
    const dispatch = useAppDispatch()
    const { getError, handleRemoveError } = useErrorMessages()
    const contactData = useAppSelector((state) => state.order.contact)
    const { language, isCreatorMode } = useApp()

    const { data: sessionData, isFetching: isSessionDataProcessing } =
        useGetUserSessionQuery({ _locale: language })

    const handleInputChange = (field: ContactField, value: string) => {
        dispatch(setContactInfo({ field, value }))
    }

    const renderField = ({
        id,
        label,
        placeholder,
        type,
        autofill,
        isRequired = false,
        infoMessage,
    }: {
        id: ContactField
        label: string
        placeholder?: string
        type?: InputType
        autofill?: string
        isRequired?: boolean
        infoMessage?: string
    }) => (
        <Input
            type={type || 'text'}
            id={id}
            onBlurChange={(value) => handleInputChange(id, value)}
            onValueChange={(value) => {
                handleRemoveError(id)
                handleInputChange(id, value)
            }}
            label={label}
            placeholder={placeholder}
            isError={!!getError(id)}
            errorMessage={getError(id)}
            autofill={autofill}
            value={contactData[`${id}`]}
            isRequired={isRequired}
            infoMessage={infoMessage}
            testId={`payment-field-contact-${id}`}
        />
    )

    useEffect(() => {
        if (sessionData && !isSessionDataProcessing) {
            const { name, surname, email } = sessionData
            name && handleInputChange('name', name)
            surname && handleInputChange('surname', surname)
            email && handleInputChange('email', email)
        }
    }, [isSessionDataProcessing])

    return (
        <Section>
            <Text variant="heading2">
                <FormattedMessage
                    id="order.contact.section_caption"
                    defaultMessage="Dane kontaktowe"
                />
            </Text>
            <ContactFormWrapper gap={32} pt={[32, 40]} pb={40}>
                {renderField({
                    id: 'name',
                    label: formatMessage({
                        id: 'global.input.first_name_label',
                        defaultMessage: 'Imię',
                    }),
                    placeholder: formatMessage({
                        id: 'global.input.first_name_placeholder',
                        defaultMessage: 'Imię',
                    }),
                    autofill: 'given-name',
                    isRequired: true,
                })}
                {renderField({
                    id: 'surname',
                    label: formatMessage({
                        id: 'global.input.last_name_label',
                        defaultMessage: 'Nazwisko',
                    }),
                    placeholder: formatMessage({
                        id: 'global.input.last_name_placeholder',
                        defaultMessage: 'Nazwisko',
                    }),
                    autofill: 'family-name',
                    isRequired: true,
                })}
                {renderField({
                    id: 'email',
                    type: 'email',
                    label: formatMessage({
                        id: 'global.input.email_label',
                        defaultMessage: 'Adres e-mail',
                    }),
                    placeholder: formatMessage({
                        id: 'global.input.email_placeholder',
                        defaultMessage: 'Adres e-mail',
                    }),
                    autofill: 'email',
                    isRequired: true,
                })}
                <Input
                    type="email"
                    id="confirmEmail"
                    onValueChange={(value) => {
                        handleInputChange('confirmEmail', value)
                    }}
                    onBlurChange={() => {
                        handleRemoveError('confirmEmail')
                    }}
                    isError={!!getError('confirmEmail')}
                    errorMessage={
                        getError('confirmEmail') ||
                        formatMessage({
                            id: 'global.input.error_email_match_verification',
                            defaultMessage: 'Adresy email nie pasują do siebie',
                        })
                    }
                    label={formatMessage({
                        id: 'global.input.confirm_email_label',
                        defaultMessage: 'Potwierdź adres e-mail',
                    })}
                    placeholder={formatMessage({
                        id: 'global.input.confirm_email_placeholder',
                        defaultMessage: 'Potwierdź adres e-mail',
                    })}
                    autofill="email"
                    isRequired
                />
                {renderField({
                    id: 'phone',
                    type: 'tel',
                    label: formatMessage({
                        id: 'global.input.phone_label',
                        defaultMessage: 'Numer telefonu',
                    }),
                    placeholder: formatMessage({
                        id: 'global.input.phone_placeholder',
                        defaultMessage: 'Numer telefonu',
                    }),
                    autofill: 'tel',
                    isRequired: isCreatorMode,
                    infoMessage: formatMessage({
                        id: 'global.input.phone_info_message',
                        defaultMessage:
                            'Numer telefonu, wraz z kodem kraju, np. 48 \u200B123 \u200B456 \u200B789',
                    }),
                })}
            </ContactFormWrapper>
            <Invoice />
        </Section>
    )
}

export default ContactData
